import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { Button, Skeleton } from "@mui/material";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { RootState, StatusState } from "store/types";

import { useAppDispatch, useAppSelector } from "store/hooks";

import { Project } from "types";
import {
  ArrowIcon,
  CenterPageSection,
  CreateProjectButton,
  FundraisingBarContainer,
  FundraisingBarFill,
  FundraisingInfo,
  ProjectCard,
  ProjectCircle,
  ProjectDate,
  ProjectFundraisingText,
  ProjectInfoLeft,
  ProjectInfoRight,
  ProjectName,
  ProjectNoneCreatedDiv,
  ProjectNoneSubtitle,
  ProjectNoneTitle,
  ProjectsHeader,
  ProjectsListContainer,
  ProjectsPageContainer,
  ProjectsTitle,
  ProjectText,
  Sections,
} from "./Project.styles";
import { ProjectSkeletonCard } from "./ProjectSkeletonCard";
import { fetchProjectsByUserId, fetchProjectByProgramId } from "store/projects/projectsList/projectsList.actions";
import { selectProjectsList } from "store/projects/projectsList/projectsList.selectors";

const Projects = () => {
  const publicUserId = Number(useParams().publicUserId);
  const programId = Number(useParams().programId);
  const { data: projects, statusState } = useAppSelector(selectProjectsList);

  const auth = useSelector((state: RootState) => state.auth);
  const userType = useAppSelector((state) => state.user.user_type);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user_id = publicUserId || auth.userId;

  useEffect(() => {
    if (programId) {
      dispatch(fetchProjectByProgramId(programId));
    } else if (user_id) {
      dispatch(fetchProjectsByUserId(user_id));
    }
  }, [programId, user_id, dispatch]);

  const handleShowProjectInfo = (id: number) => {
    navigate(`${id}`);
  };

  const handleOpenCreateProject = () => {
    navigate("new");
  };

  const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
    });
  };

  return (
    <Sections>
      <CenterPageSection>
        <ProjectsPageContainer>
          {statusState===StatusState.LOADING ? (
            <>
              <ProjectsHeader>
                <Skeleton variant="text" width={180} height={30} />
              </ProjectsHeader>

              {[...Array(3)].map((_, i) => (
                <ProjectSkeletonCard key={i} />
              ))}
            </>
          ) : (
            <>
              <ProjectsHeader>
                <ProjectsTitle>Current Projects</ProjectsTitle>

                {userType === "organization" && !publicUserId && (
                  <CreateProjectButton onClick={handleOpenCreateProject}>
                    + Create New Project
                  </CreateProjectButton>
                )}
              </ProjectsHeader>

              {projects.length <= 0 && !Boolean(publicUserId) && (
                <ProjectNoneCreatedDiv>
                  <ProjectNoneTitle>
                    Oops! No projects created yet
                  </ProjectNoneTitle>
                  <ProjectNoneSubtitle>
                    Projects can be created to host different events and causes
                  </ProjectNoneSubtitle>
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={handleOpenCreateProject}
                  >
                    Create New Project
                  </Button>
                </ProjectNoneCreatedDiv>
              )}

              {projects.length <= 0 && Boolean(publicUserId) && (
                <ProjectNoneCreatedDiv>
                  <ProjectNoneTitle>
                    This user has not created any projects yet
                  </ProjectNoneTitle>
                  <ProjectNoneSubtitle>
                    Projects can host different events and causes
                  </ProjectNoneSubtitle>
                </ProjectNoneCreatedDiv>
              )}

              {projects.length > 0 && (
                <ProjectsListContainer>
                  {projects.map((proj) => {
                    const start = proj.start_date_time
                      ? formatDate(proj.start_date_time)
                      : "N/A";
                    const end = proj.end_date_time
                      ? formatDate(proj.end_date_time)
                      : "N/A";

                    let fundraisingPercent = proj.total_goal
                      ? Math.round(
                        ((proj.total_raised ?? 0) * 100) / proj.total_goal
                      )
                      : null;
                    if (fundraisingPercent && fundraisingPercent > 100) {
                      fundraisingPercent = 100;
                    }

                    return (
                      <ProjectCard
                        key={proj.id}
                        onClick={() =>
                          proj.id !== undefined &&
                          handleShowProjectInfo(proj.id)
                        }
                        onKeyDown={(e) => {
                          /* accesible con ENTER/ESPACIO */
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            proj.id !== undefined &&
                              handleShowProjectInfo(proj.id);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                      >
                        <ProjectInfoLeft>
                          <ProjectCircle color="#FE2F67" />
                          <ProjectText>
                            <ProjectName>{proj.project_name}</ProjectName>
                            <ProjectDate>
                              <AccessTimeIcon
                                sx={{ fontSize: "14px", color: "#9C9C9C" }}
                              />
                              &nbsp; {start} - {end}
                            </ProjectDate>
                          </ProjectText>
                        </ProjectInfoLeft>

                        <ProjectInfoRight>
                          {fundraisingPercent === null ? (
                            <>
                              <ProjectFundraisingText>
                                No Fundraising Campaign
                              </ProjectFundraisingText>
                              <ArrowIcon />
                            </>
                          ) : (
                            <>
                              <FundraisingInfo>
                                <ProjectFundraisingText>
                                  Fundraising Progress
                                </ProjectFundraisingText>
                                <FundraisingBarContainer>
                                  <FundraisingBarFill
                                    percentage={fundraisingPercent ?? 0}
                                  />
                                </FundraisingBarContainer>
                                <ProjectFundraisingText>
                                  {fundraisingPercent !== null
                                    ? parseInt(fundraisingPercent.toFixed(2))
                                    : 0}
                                  %
                                </ProjectFundraisingText>
                              </FundraisingInfo>

                              <ArrowIcon
                                onClick={() =>
                                  proj.id !== undefined &&
                                  handleShowProjectInfo(proj.id)
                                }
                              />
                            </>
                          )}
                        </ProjectInfoRight>
                      </ProjectCard>
                    );
                  })}
                </ProjectsListContainer>
              )}
            </>
          )}
        </ProjectsPageContainer>
      </CenterPageSection>
    </Sections>
  );
};

export default Projects;
